import {
  Component,
  OnInit,
  OnDestroy,
  Optional,
  Inject,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import {
  AccessRenewalService,
  normalizeUserExpiryRecords,
  UserExpirySaveRecord,
} from '../access-renewal.service';

export interface UserExpiryReportModalData {
  gridPayload: any;
  clientKey: string | number;
}

@Component({
  selector: 'gtw-user-expiry-report-modal',
  templateUrl: './user-expiry-report-modal.component.html',
  styleUrls: ['./user-expiry-report-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe],
  standalone: false
})
export class UserExpiryReportModalComponent implements OnInit, OnDestroy {
  title = 'User Expiry Report';
  minimise = false;
  saving = false;
  userExpiryRecords: any[] = [];
  /** yyyy-MM-dd for native date input */
  startDate = '';

  private clientKey: string | number = '';
  private destroy$ = new Subject<void>();

  constructor(
    @Optional() private dialogRef: MatDialogRef<UserExpiryReportModalComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private data: UserExpiryReportModalData,
    private accessRenewalService: AccessRenewalService,
    private snackbar: GtwSnackbarService,
    private datePipe: DatePipe,
    private cdr: ChangeDetectorRef,
  ) {
    this.clientKey = data?.clientKey ?? '';
    this.userExpiryRecords = normalizeUserExpiryRecords(data?.gridPayload);
  }

  ngOnInit(): void {
    this.startDate = this.formatDateForInput(new Date());
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  removeRow(index: number): void {
    this.userExpiryRecords.splice(index, 1);
    this.cdr.detectChanges();
  }

  save(): void {
    if (this.userExpiryRecords.length < 1 || this.saving) {
      return;
    }

    const expiryDate = this.formatExpiryDateForSave(this.startDate);
    if (!expiryDate) {
      this.snackbar.openSnackBar('Please select a valid expiry date.', 'gtw-snackbar--red');
      return;
    }

    const userReports: UserExpirySaveRecord[] = this.userExpiryRecords.map((item) => ({
      client_key: this.clientKey,
      user_sso: item.SSO_ID,
      expiry_date: expiryDate,
    }));

    this.saving = true;
    this.cdr.detectChanges();

    this.accessRenewalService
      .saveUserExpiryReport(userReports)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.saving = false;
          if (res?.callSuccess === '1') {
            this.snackbar.openSnackBar(
              'User Expiry Dates updated successfully.',
              'gtw-snackbar--green',
            );
            this.dialogRef?.close('saved');
          } else {
            this.snackbar.openSnackBar(
              res?.errorMessage || 'Save failed',
              'gtw-snackbar--red',
            );
            this.cdr.detectChanges();
          }
        },
        error: () => {
          this.saving = false;
          this.snackbar.openSnackBar(
            'An error has occurred. If this continues please contact support.',
            'gtw-snackbar--red',
          );
          this.cdr.detectChanges();
        },
      });
  }

  cancel(): void {
    this.dialogRef?.close();
  }

  private formatDateForInput(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private formatExpiryDateForSave(isoDate: string): string | null {
    if (!isoDate) {
      return null;
    }
    const parts = isoDate.split('-').map(Number);
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
      return null;
    }
    const date = new Date(parts[0], parts[1] - 1, parts[2]);
    return this.datePipe.transform(date, 'MM/dd/yyyy') ?? null;
  }
}
